/**
 * PATH: packages/email-hub/src/feature/lib/draftAttachmentStore.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-DRAFTATTACHMENTSTORE-001
 * PURPOSE: IndexedDB-backed persistence for compose draft ATTACHMENTS.
 *          The text part of a draft (recipients/subject/body) lives in
 *          localStorage via draftStore.ts, but File bytes can't be
 *          JSON-serialised — so until now, closing and reopening a
 *          compose window silently dropped every attached file while
 *          keeping the body. This store keeps the actual Blob bytes per
 *          draft slot so a resumed draft comes back WITH its files.
 * WHY:     Operator-visible parity with Gmail/Outlook: an autosaved
 *          draft must retain its attachments. localStorage tops out at
 *          ~5MB and can't hold binary; IndexedDB stores Blobs natively
 *          and comfortably holds the 35MB message cap.
 * RELATED:
 *   - draftStore.ts: text half of the same draft (shared slot key).
 *   - pages/useComposePage.ts: the sole consumer (autosave + resume).
 */

import { tlsKey } from '../../_internal/utils/tenantStorage'

// Namespaced per tenant the same way draftStore keys are, so SAM and
// newmark never share an attachment store even though both run the same
// bundle bytes. `tlsKey` resolves e.g. 'sam.email.compose.draftAttachments.v1'.
const DB_NAME = tlsKey('email.compose.draftAttachments.v1')
const STORE_NAME = 'attachments'
const DB_VERSION = 1

/** Mirror of draftStore's DRAFT_MAX_AGE_MS so orphaned blobs age out too. */
const MAX_AGE_MS = 14 * 24 * 3600 * 1000

interface StoredFile {
  name: string
  type: string
  lastModified: number
  blob: Blob
}

interface AttachmentRecord {
  slotKey: string
  files: StoredFile[]
  saved_at: number
}

function idbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined'
  } catch {
    return false
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'slotKey' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function txComplete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onabort = () => reject(tx.error)
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * Persist the current attachment set for a draft slot. Passing an empty
 * array deletes the record so we never leave a stale blob behind once
 * the operator removes the last file. Fail-soft: a storage error never
 * throws into the autosave path.
 */
export async function saveDraftAttachments(slotKey: string, files: File[]): Promise<void> {
  if (!idbAvailable()) return
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    if (files.length === 0) {
      store.delete(slotKey)
    } else {
      const record: AttachmentRecord = {
        slotKey,
        saved_at: Date.now(),
        // A File IS a Blob, so structured-clone stores the bytes as-is.
        // We snapshot the metadata explicitly so reconstruction doesn't
        // depend on File-subclass fields surviving the clone.
        files: files.map((f) => ({
          name: f.name,
          type: f.type,
          lastModified: f.lastModified,
          blob: f.slice(0, f.size, f.type),
        })),
      }
      store.put(record)
    }
    await txComplete(tx)
    db.close()
  } catch {
    // Quota exceeded / private-mode / disabled — the operator never
    // opted into autosave, so a failure here must stay invisible.
  }
}

/**
 * Load the persisted attachments for a slot, reconstructed as File
 * objects so they flow straight back into the multipart send path.
 * Returns [] when missing, stale, or on any error.
 */
export async function loadDraftAttachments(slotKey: string): Promise<File[]> {
  if (!idbAvailable()) return []
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readonly')
    const store = tx.objectStore(STORE_NAME)
    const record = await new Promise<AttachmentRecord | undefined>((resolve, reject) => {
      const req = store.get(slotKey)
      req.onsuccess = () => resolve(req.result as AttachmentRecord | undefined)
      req.onerror = () => reject(req.error)
    })
    db.close()
    if (!record || !Array.isArray(record.files)) return []
    if (Date.now() - (record.saved_at || 0) > MAX_AGE_MS) {
      void clearDraftAttachments(slotKey)
      return []
    }
    return record.files
      .filter((sf) => sf && sf.blob instanceof Blob)
      .map((sf) => new File([sf.blob], sf.name || 'attachment', {
        type: sf.type || sf.blob.type || 'application/octet-stream',
        lastModified: sf.lastModified || Date.now(),
      }))
  } catch {
    return []
  }
}

/** Drop the persisted attachments for a slot (send / discard). */
export async function clearDraftAttachments(slotKey: string): Promise<void> {
  if (!idbAvailable()) return
  try {
    const db = await openDB()
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(slotKey)
    await txComplete(tx)
    db.close()
  } catch {
    // noop — see saveDraftAttachments.
  }
}
